import React, { RefObject } from 'react';
import cs from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';
import { injectStoresV2 } from '../../../stores/injectStoresV2';
import { SearchWithSuggestionsStore } from '../../../stores/SearchWithSuggestionsStore';
import { HeaderStore } from '../../../stores/HeaderStore';
import { dropdownId } from '../SuggestionsDropdown/SuggestionsDropdown';
import styles from './SearchInput.scss';

type SearchInputProps = {
  searchWithSuggestionsStore: SearchWithSuggestionsStore;
  headerStore: HeaderStore;
  dataHook: string;
  forwardedRef: RefObject<HTMLInputElement>;
};

const SearchInputCmp: React.FC<SearchInputProps> = ({
  dataHook,
  searchWithSuggestionsStore,
  headerStore,
  forwardedRef,
}) => {
  const { t } = useTranslation();
  const {
    preSelectNextSuggestion,
    preSelectPreviousSuggestion,
    setHighlightedSuggestionIndex,
    isDropdownOpened,
    preSelectedSuggestion,
  } = searchWithSuggestionsStore;
  const { searchCriteria, isSticky, setSearchInputFocused, setSearchCriteria } = headerStore;
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpened) {
      return;
    }

    if (event.key === 'ArrowDown') {
      preSelectNextSuggestion();
      event.preventDefault();
    }
    if (event.key === 'ArrowUp') {
      preSelectPreviousSuggestion();
      event.preventDefault();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHighlightedSuggestionIndex(null);
    setSearchCriteria(event.target.value);
  };

  const inputValue = preSelectedSuggestion?.text ?? searchCriteria;

  return (
    <input
      id="criteria"
      name="criteria"
      ref={forwardedRef}
      className={cs(styles.searchInput, {
        [styles.fullHeight]: !isSticky,
        [styles.extraVerticalPadding]: isSticky,
        [styles.smallerText]: isSticky,
        [styles.thinSearchInput]: true,
      })}
      autoComplete="off"
      autoFocus={!isSticky && !searchCriteria}
      onChange={handleInputChange}
      onFocus={() => setSearchInputFocused(true)}
      onKeyDown={handleInputKeyDown}
      aria-label={t('search.label')}
      data-hook={dataHook}
      data-bi-element="search"
      data-bi-element-value="NULL"
      type="search"
      value={inputValue}
      placeholder={t('header.searchPlaceholder')}
      aria-autocomplete="list"
      aria-haspopup="true"
      role="combobox"
      aria-controls={dropdownId}
      aria-expanded={isDropdownOpened}
    />
  );
};

export const SearchInput = injectStoresV2('searchWithSuggestionsStore', 'headerStore')(SearchInputCmp);
